import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlus, Copy, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePaginated } from "@/components/Paginator";

const ROLES = [
  { id: "admin", label: "Admin" },
  { id: "accountant", label: "Akuntan" },
  { id: "sales", label: "Sales" },
  { id: "viewer", label: "Viewer (Read Only)" },
];

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "viewer" });
  const [lastLink, setLastLink] = useState("");
  const isAdmin = user?.role === "owner" || user?.role === "admin";

  const load = () => {
    api.get("/team/users").then((r) => setUsers(r.data));
    api.get("/team/invites").then((r) => setInvites(r.data));
  };
  useEffect(() => { load(); }, []);
  const pg = usePaginated(users);

  const invite = async () => {
    try {
      const r = await api.post("/team/invite", form);
      const url = `${window.location.origin}${r.data.invite_link}`;
      setLastLink(url);
      toast.success("Undangan dibuat");
      setForm({ email: "", full_name: "", role: "viewer" });
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal invite"); }
  };

  const changeRole = async (id, role) => {
    await api.patch(`/team/users/${id}/role`, { role });
    toast.success("Role diubah");
    load();
  };

  const removeUser = async (id) => {
    await api.delete(`/team/users/${id}`);
    toast.success("User dihapus");
    load();
  };

  const cancelInvite = async (id) => {
    await api.delete(`/team/invites/${id}`);
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl" data-testid="team-page">
      <div className="flex justify-between items-baseline">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Tim</h1>
          <p className="text-sm text-slate-500 mt-1">Undang anggota tim & atur perannya.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-amber-600 hover:bg-amber-700" data-testid="invite-btn"><UserPlus size={16} className="mr-1" /> Undang</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Undang Anggota</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nama</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="inv-name-input" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inv-email-input" /></div>
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="inv-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={invite} className="w-full bg-slate-900" data-testid="inv-submit-btn">Buat Undangan</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {lastLink && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-md p-4">
          <div className="text-xs uppercase tracking-wider text-emerald-700 mb-2">Link Undangan (bagikan ke user):</div>
          <div className="flex items-center gap-2 bg-white p-3 rounded border border-emerald-200 font-mono-plex text-xs">
            <span className="flex-1 truncate">{lastLink}</span>
            <button onClick={() => { navigator.clipboard.writeText(lastLink); toast.success("Disalin"); }}><Copy size={14} /></button>
          </div>
        </div>
      )}

      <h2 className="font-serif-display text-2xl font-medium text-slate-900 mt-8 mb-3">Anggota</h2>
      <div className="bg-white border border-slate-200 rounded-md" data-testid="members-table">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {pg.paged.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.full_name}</TableCell>
                <TableCell className="text-slate-600">{u.email}</TableCell>
                <TableCell>
                  {u.role === "owner" || !isAdmin || u.id === user?.id ? (
                    <span className="text-sm capitalize">{u.role}</span>
                  ) : (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger className="h-8 text-xs w-32" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell>
                  {isAdmin && u.role !== "owner" && u.id !== user?.id && (
                    <button onClick={() => removeUser(u.id)} className="text-slate-400 hover:text-rose-600" data-testid={`remove-user-${u.id}`}><Trash size={14} /></button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pg.pager}
      </div>

      {invites.length > 0 && (
        <>
          <h2 className="font-serif-display text-2xl font-medium text-slate-900 mt-8 mb-3">Undangan Tertunda</h2>
          <div className="bg-white border border-slate-200 rounded-md">
            <Table>
              <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {invites.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>{i.full_name}</TableCell>
                    <TableCell className="text-slate-600">{i.email}</TableCell>
                    <TableCell className="capitalize">{i.role}</TableCell>
                    <TableCell><button onClick={() => cancelInvite(i.id)} className="text-slate-400 hover:text-rose-600"><Trash size={14} /></button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
